import * as d3 from 'd3';
import { ZoomMode } from '../../models/Mode';
import { TimeAxis, VerticalAxis } from './axes/index';
import { ILogger, LoggerFactory } from '../../helpers/helpers';

export class Zoom {
    private _container: d3.Selection<any>;
    private _isEnabled: boolean;
    private _isSelecting = false;
    private _isZooming = false;
    private _logger: ILogger;
    private _mode: ZoomMode;
    private _selection: d3.Selection<any>;
    private _unzoomedData: any[];
    private _xAxis: TimeAxis;
    private _yAxis: VerticalAxis;
    private _zoom: d3.behavior.Zoom<any>;
    private _zoomRectangle: d3.Selection<any>;

    private _dispatch: any;

    constructor(container: d3.Selection<any>, xAxis: TimeAxis, yAxis: VerticalAxis) {
        this._logger = LoggerFactory.getLogger('WideTech.Charting.zoom', false, 0);

        this._xAxis = xAxis;
        this._yAxis = yAxis;
        this._container = container;
        const zoomGroup = container
            .append('g')
            .classed('zoom-group', true);

        this._zoomRectangle = zoomGroup
            .append('rect')
            .classed('zoom-rectangle', true)
            .attr({ 'width': 0, 'height': 0 });

        this._selection = zoomGroup
            .append('rect')
            .classed('zoom-selection', true)
            .attr({ 'width': 0, 'height': 0 });

        this._dispatch = d3.dispatch('zoom', 'unzoom', 'pan', 'mousedown', 'selection');

        let xStart: number;
        let yStart: number;
        let xMin: number;
        let yMin: number;
        this._mode = ZoomMode.Zoom;
        this._isEnabled = true;

        container.on('mousedown', () => {
            if (this._isEnabled) {
                try {
                    const { x, y } = this.getEventPosition();

                    if (this._mode === ZoomMode.Zoom) {
                        this._isZooming = true;
                        if (!this._unzoomedData) {
                            this._unzoomedData = [];
                        }
                        const xDomain = this._xAxis.scale().domain();
                        this._unzoomedData.push({
                            xDomain: xDomain,
                            yDomain: this._yAxis.scale().domain()
                        });
                        this._dispatch.mousedown(xDomain);

                        xStart = xMin = x;
                        yStart = yMin = y;

                        this._zoomRectangle.attr({
                            'x': xStart,
                            'y': yStart,
                            'width': 0,
                            'height': 0
                        });
                    } else if (this._mode === ZoomMode.Selection) {
                        this._isSelecting = true;
                        xStart = xMin = x;
                        yStart = yMin = y;
                        this._selection.attr({
                            'x': xStart,
                            'y': 0,
                            'width': 0,
                            'height': this.getHeight()
                        });
                    }
                } catch (e) {
                    this._logger.error(e);
                }
            }
        });

        container.on('mousemove', () => {
            if (this._isEnabled) {
                try {
                    const pos = this.getEventPosition();
                    if (this._isZooming) {
                        let width = pos.x - xStart;
                        let height = pos.y - yStart;
                        let x = xStart;
                        let y = yStart;
                        if (width < 0) {
                            width = Math.abs(width);
                            x = xStart - width;
                        }
                        if (height < 0) {
                            height = Math.abs(height);
                            y = yStart - height;
                        }
                        this._zoomRectangle.attr({
                            'x': x,
                            'y': y,
                            'width': width,
                            'height': height
                        });
                    } else if (this._isSelecting) {
                        let width = pos.x - xStart;
                        let x = xStart;
                        if (width < 0) {
                            width = Math.abs(width);
                            x = xStart - width;
                        }
                        this._selection.attr({
                            'x': x,
                            'width': width
                        });
                    }
                } catch (e) {
                    this._logger.error(e);
                }
            }
        });

        container.on('mouseup', () => {
            if (this._isEnabled) {
                try {
                    const { x, y } = this.getEventPosition();

                    if (this._isZooming) {
                        this._isZooming = false;

                        const minYPos = d3.min([y, yMin]);
                        const maxYPos = d3.max([y, yMin]);
                        if (x !== xMin && y !== yMin) {
                            const bd = this._xAxis.scale().invert(xMin);
                            const ed = this._xAxis.scale().invert(x);
                            let min = this._yAxis.scale().invert(maxYPos);
                            let max = this._yAxis.scale().invert(minYPos);
                            this._zoomRectangle.attr({
                                'width': 0,
                                'height': 0
                            });
                            const beginDate = d3.min([bd, ed], d => d);
                            const endDate = d3.max([bd, ed], d => d);
                            const tmp = d3.min([min, max], d => d);
                            max = d3.max([min, max], d => d);
                            min = tmp;
                            this._dispatch.zoom({
                                beginDate: beginDate,
                                endDate: endDate,
                                min: min,
                                max: max,
                                minPos: minYPos,
                                maxPos: maxYPos
                            });
                        }
                    } else if (this._isSelecting) {
                        this._isSelecting = false;                        
                        const bd = this._xAxis.scale().invert(xMin);
                        const ed = this._xAxis.scale().invert(x);
                        const beginDate = d3.min([bd, ed], d => d);
                        const endDate = d3.max([bd, ed], d => d);
                        this._dispatch.selection({beginDate, endDate});                        
                    }
                } catch (e) {
                    this._logger.error(e);
                }
            }
        });
    }

    dispatch() {
        return this._dispatch;
    }

    resetZoom(group?: d3.Selection<any>) {
        const unzoomed = this._unzoomedData.pop();
        if (unzoomed) {
            const beginDate = d3.min(unzoomed.xDomain);
            const endDate = d3.max(unzoomed.xDomain);
            const min = d3.min(unzoomed.yDomain);
            const max = d3.max(unzoomed.yDomain);
            if (this._unzoomedData.length === 0) {
                this.setZoomMode(group);
            }
            this.dispatch().unzoom({
                beginDate: beginDate,
                endDate: endDate,
                min: min,
                max: max
            });
        }
    }

    resetSelection(_group?: d3.Selection<any>) {
        this._isSelecting = false;
        this._selection.attr({'x': 0, 'y': 0, 'width': 0, 'height': 0});
    }

    isZoomed() {
        return !!this._unzoomedData && this._unzoomedData.length > 0;
    }

    pan(group?: d3.Selection<any>) {
        if (this._mode !== ZoomMode.Pan) {
            this._mode = ZoomMode.Pan;
            this._zoom = d3.behavior.zoom()
                .scaleExtent([1, 1])
                .on('zoom', () => {
                    const scale = this._xAxis.scale();
                    const domain = scale.domain();
                    this.dispatch().pan({
                        beginDate: domain[0],
                        endDate: domain[1]
                    });
                });
            this._zoom.x(<any>this._xAxis.scale());
            this._container.call(this._zoom);
            this.crosshair(group).classed('pan', true);
        } else {
            this.setZoomMode(group);
        }
    }

    select(group?: d3.Selection<any>) {
        if (this._mode !== ZoomMode.Selection) {
            this._mode = ZoomMode.Selection;
            this.crosshair(group).classed('selection', true);
        } else {
            this.setZoomMode(group);
        }
    }

    mode() {
        return this._mode;
    }

    isEnabled(value?: boolean): boolean {
        if (arguments.length) {
            this._isEnabled = value;
            d3.select('.crosshair-rectangle').classed('zoom-disabled', !value);
        }
        return this._isEnabled;
    }

    private getEventPosition(): { x: number; y: number } {
        const position = d3.mouse((d3.event as Event).target);
        return {
            x: position[0],
            y: position[1]
        };
    }

    private setZoomMode(group?: d3.Selection<any>) {
        this._mode = ZoomMode.Zoom;
        this._zoom = d3.behavior.zoom().on('zoom', null);
        this._container.call(this._zoom);
        this.crosshair(group)
            .classed('pan', false)
            .classed('selection', false);
    }

    private crosshair(group?: d3.Selection<any>): d3.Selection<any> {
        return group
            ? group.select('.crosshair-rectangle')
            : d3.select('.crosshair-rectangle');
    }

    private getHeight(): number {
        return Number.parseFloat(this._container.select('.clip-path-rectangle').attr('height'));
    }
}